import type { CSSProperties } from 'react';

import classNames from 'classnames';

import { ImageUpload } from '@/ui/ImageUpload';
import SimpleModal from '@/ui/SimpleModal';

import { ButtonElement } from '../shared/buttonElement/ButtonElement';
import { Input } from '../shared/inputElement/Input';
import { SectionElement } from '../shared/sectionElement/SectionElement';
import type { UpdateProfileModalElementProps } from './UpdateProfileModalElement.types';

export function UpdateProfileModalElement({
  element,
  isOpen,
  onOpenChange,
  onCancel,
  translations,
  isLoading,
  canResendEmail,
  onClickResendEmail,
  onAvatarSelect,
}: UpdateProfileModalElementProps) {
  const data = element?.attrs?.data || {};

  const generatedStyle = {
    '--overlay-background-color': '#00000030',
    '--modal-close-button-color': undefined,
    '--modal-close-button-placement-top': '24px',
    '--modal-close-button-placement-right': '24px',
  } as CSSProperties;

  return (
    <SimpleModal
      isOpen={isOpen}
      onClose={onOpenChange}
      className="shadow-lg sm:rounded-md overflow-hidden"
      style={generatedStyle}
      showCloseButton={false}
      showRoundedBorders={false}
      shouldCloseOnOutsideClick={false}
    >
      <SectionElement
        className="bg-white w-full sm:w-[460px]"
        title={translations?.['manage.update_profile_modal.title']}
        subTitle={translations?.['manage.update_profile_modal.description']}
      >
        <div className="flex flex-col gap-6">
          <ImageUpload
            currentImageUrl={data.avatarUrl}
            onFileSelect={onAvatarSelect || (() => {})}
            shape="circle"
            size={108}
            disabled={isLoading}
            isLoading={isLoading}
            alt={translations?.['manage.update_profile_modal.form.avatar_alt']}
            helperText={translations?.['manage.update_profile_modal.form.avatar_helper_text']}
          />
          <div className="flex flex-col gap-4">
            <Input
              id="update-profile-email"
              name="email"
              type="email"
              value={data.email}
              placeholder={translations?.['manage.update_profile_modal.form.email_placeholder']}
              label={translations?.['manage.update_profile_modal.form.email_label']}
              helperText={
                canResendEmail ? translations?.['manage.update_profile_modal.form.email_change_helper_text'] : undefined
              }
            />
            <Input
              id="update-profile-first-name"
              name="first_name"
              type="text"
              value={data.firstName}
              placeholder={translations?.['manage.update_profile_modal.form.first_name_placeholder']}
              label={translations?.['manage.update_profile_modal.form.first_name_label']}
            />
            <Input
              id="update-profile-last-name"
              name="last_name"
              type="text"
              value={data.lastName}
              placeholder={translations?.['manage.update_profile_modal.form.last_name_placeholder']}
              label={translations?.['manage.update_profile_modal.form.last_name_label']}
            />
            <div className={classNames('flex mt-4', canResendEmail ? 'justify-between' : 'justify-end')}>
              {canResendEmail && (
                <ButtonElement variant="flush" onClick={onClickResendEmail} disabled={isLoading}>
                  {translations?.['manage.update_profile_modal.resend_email']}
                </ButtonElement>
              )}
              <div className="flex gap-2">
                <ButtonElement variant="default" disabled={isLoading} onClick={onCancel}>
                  {translations?.['manage.update_profile_modal.cancel']}
                </ButtonElement>
                <ButtonElement type="submit" variant="primary" disabled={isLoading}>
                  {translations?.['manage.update_profile_modal.save']}
                </ButtonElement>
              </div>
            </div>
          </div>
        </div>
      </SectionElement>
    </SimpleModal>
  );
}
